import { createSelector } from "@reduxjs/toolkit";
import {
  districtsSelector,
  isOpenDialogSelector,
  wardsSelector,
} from "redux-store/static/redux/selector";
import { RootState } from "redux-store/store";

import { isEmpty, keys } from "lodash";
import { getChangedProperties } from "helper/utils";
import { DIALOG } from "helper/enum/dialogEnum";

const personnelInformationSelector = (state: RootState) =>
  state.personnelInformation;

// residence information
const residenceInformationSelector = createSelector(
  personnelInformationSelector,
  (state) => state.getResidenceInformation
);

export const residenceInformationDataSelector = createSelector(
  residenceInformationSelector,
  (state) => state.data
);

export const residenceInformationStatusSelector = createSelector(
  residenceInformationSelector,
  (state) => state.status
);

// work information
const workInformationSelector = createSelector(
  personnelInformationSelector,
  (state) => state.getWorkInformation
);

export const workInformationDataSelector = createSelector(
  workInformationSelector,
  (state) => state.data
);

export const workInformationStatusSelector = createSelector(
  workInformationSelector,
  (state) => state.status
);

// basic information
const basicInformationSelector = createSelector(
  personnelInformationSelector,
  (state) => state.getBasicInformation
);

export const basicInformationDataSelector = createSelector(
  basicInformationSelector,
  (state) => state.data
);

export const basicInformationStatusSelector = createSelector(
  basicInformationSelector,
  (state) => state.status
);

// bank information
const bankInformationSelector = createSelector(
  personnelInformationSelector,
  (state) => state.getBankInformation
);

export const bankInformationDataSelector = createSelector(
  bankInformationSelector,
  (state) => state.data
);

export const bankInformationStatusSelector = createSelector(
  bankInformationSelector,
  (state) => state.status
);

// marital information
const maritalInformationSelector = createSelector(
  personnelInformationSelector,
  (state) => state.getMaritalInformation
);

export const maritalInformationDataSelector = createSelector(
  maritalInformationSelector,
  (state) => state.data
);

export const maritalInformationStatusSelector = createSelector(
  maritalInformationSelector,
  (state) => state.status
);

// education information
const educationInformationSelector = createSelector(
  personnelInformationSelector,
  (state) => state.getEducationInformation
);

export const educationInformationDataSelector = createSelector(
  educationInformationSelector,
  (state) => state?.data
);

export const educationInformationStatusSelector = createSelector(
  educationInformationSelector,
  (state) => state.status
);

// language information
const languageInformationSelector = createSelector(
  personnelInformationSelector,
  (state) => state.getLanguageInformation
);

export const languageInformationDataSelector = createSelector(
  languageInformationSelector,
  (state) => state.data
);

export const languageInformationStatusSelector = createSelector(
  languageInformationSelector,
  (state) => state.status
);

// certification information
const certificationInformationSelector = createSelector(
  personnelInformationSelector,
  (state) => state.getCertificationInformation
);

export const certificationInformationDataSelector = createSelector(
  certificationInformationSelector,
  (state) => state.data
);

export const certificationInformationStatusSelector = createSelector(
  certificationInformationSelector,
  (state) => state.status
);

// certification Professional information
const certificationProfessionalSelector = createSelector(
  personnelInformationSelector,
  (state) => state.getCertificationProfessional
);

export const certificationProfessionalDataSelector = createSelector(
  certificationProfessionalSelector,
  (state) => state.data
);

export const certificationProfessionalStatusSelector = createSelector(
  certificationProfessionalSelector,
  (state) => state.status
);

// avatar
const avatarSelector = createSelector(
  personnelInformationSelector,
  (state) => state.getAvatar
);

export const avatarDataSelector = createSelector(
  avatarSelector,
  (state) => state.data
);

export const avatarStatusSelector = createSelector(
  avatarSelector,
  (state) => state.status
);

// signature
const signatureSelector = createSelector(
  personnelInformationSelector,
  (state) => state.getSignature
);

export const signatureDataSelector = createSelector(
  signatureSelector,
  (state) => state.data
);

export const signatureStatusSelector = createSelector(
  signatureSelector,
  (state) => state.status
);

// control
const controlSelector = createSelector(
  personnelInformationSelector,
  (state) => state.control
);

export const isEditingPersonalInformationSelector = createSelector(
  controlSelector,
  (state) => state.isEditing
);

// form edit bank information
const formEditBankInformationSelector = createSelector(
  personnelInformationSelector,
  (state) => state.formEditBankInformation
);

export const formEditBankInformationDataSelector = createSelector(
  formEditBankInformationSelector,
  (state) => state.formData
);

export const bankInformationCurrentDataSelector = createSelector(
  formEditBankInformationSelector,
  (state) => state.currentData
);

export const bankInformationChangeDataSelector = createSelector(
  formEditBankInformationSelector,
  (state) => state.changedData
);

export const bankInformationChangeKeyDataSelector = createSelector(
  bankInformationChangeDataSelector,
  (state) => keys(state)
);

export const isBankInformationEditedSelector = createSelector(
  bankInformationChangeDataSelector,
  (state) => !isEmpty(state)
);

// form edit marital information
const formEditMaritalInformationSelector = createSelector(
  personnelInformationSelector,
  (state) => state.formEditMaritalInformation
);

export const formEditMaritalInformationDataSelector = createSelector(
  formEditMaritalInformationSelector,
  (state) => state.formData
);

export const maritalInformationCurrentDataSelector = createSelector(
  formEditMaritalInformationSelector,
  (state) => state.currentData
);

export const maritalInformationChangeDataSelector = createSelector(
  formEditMaritalInformationSelector,
  (state) => state.changedData
);

export const maritalInformationChangeKeySelector = createSelector(
  maritalInformationChangeDataSelector,
  (state) => keys(state)
);

export const isMaritalInformationEditedSelector = createSelector(
  maritalInformationChangeDataSelector,
  (state) => !isEmpty(state)
);

// form edit education information
const formEditEducationInformationSelector = createSelector(
  personnelInformationSelector,
  (state) => state.formEditEducationInformation
);

export const formEditEducationInformationDataSelector = createSelector(
  formEditEducationInformationSelector,
  (state) => state.formData
);

export const educationInformationChangeDataSelector = createSelector(
  formEditEducationInformationSelector,
  (state) => state.changedData
);

export const educationInformationChangeKeySelector = createSelector(
  educationInformationChangeDataSelector,
  (state) => keys(state)
);

export const isEducationInformationEditedSelector = createSelector(
  educationInformationChangeDataSelector,
  (state) => !isEmpty(state)
);

export const educationInformationCurrentDataSelector = createSelector(
  formEditEducationInformationSelector,
  (state) => state.currentData
);

// form edit basic information
const formEditBasicInformationSelector = createSelector(
  personnelInformationSelector,
  (state) => state.formEditBasicInformation
);

export const formEditBasicInformationDataSelector = createSelector(
  formEditBasicInformationSelector,
  (state) => state.formData
);

export const basicInformationCurrentDataSelector = createSelector(
  formEditBasicInformationSelector,
  (state) => state.currentData
);

export const basicInformationChangeDataSelector = createSelector(
  formEditBasicInformationSelector,
  (state) => state.changedData
);

export const basicInformationChangeKeySelector = createSelector(
  basicInformationChangeDataSelector,
  (state) => keys(state)
);

export const isBasicInformationEditedSelector = createSelector(
  basicInformationChangeDataSelector,
  (state) => !isEmpty(state)
);

// form edit residence information
const formEditResidenceInformationSelector = createSelector(
  personnelInformationSelector,
  (state) => state.formEditResidenceInformation
);

export const formEditResidenceInformationDataSelector = createSelector(
  formEditResidenceInformationSelector,
  (state) => state.formData
);

export const residenceInformationDataChangeSelector = createSelector(
  formEditResidenceInformationSelector,
  (state) => state.changedData
);

export const isResidenceInformationEditedSelector = createSelector(
  residenceInformationDataChangeSelector,
  (state) => !isEmpty(state)
);

export const residenceInformationCurrentDataSelector = createSelector(
  formEditResidenceInformationSelector,
  (state) => state.currentData
);

export const residenceInformationDataChangeKeySelector = createSelector(
  residenceInformationDataChangeSelector,
  (state) => keys(state)
);

export const selectedResidentProvinceSelector = createSelector(
  formEditResidenceInformationDataSelector,
  (state) => state.residentProvinceCode
);

export const residentDistrictOptionsSelector = createSelector(
  districtsSelector,
  selectedResidentProvinceSelector,
  (allDistrict, selectedProvince) => allDistrict[selectedProvince] ?? []
);

export const selectedResidentDistrictSelector = createSelector(
  formEditResidenceInformationDataSelector,
  (state) => state.residentDistrictCode
);

export const residentWardsOptionsSelector = createSelector(
  wardsSelector,
  selectedResidentProvinceSelector,
  (allWards, selectedProvince) => allWards[selectedProvince] ?? []
);

export const selectedStayingProvinceSelector = createSelector(
  formEditResidenceInformationDataSelector,
  (state) => state.stayingProvinceCode
);

export const stayingDistrictOptionsSelector = createSelector(
  districtsSelector,
  selectedStayingProvinceSelector,
  (allDistrict, selectedProvince) => allDistrict[selectedProvince] ?? []
);

export const selectedStayingDistrictSelector = createSelector(
  formEditResidenceInformationDataSelector,
  (state) => state.stayingDistrictCode
);

export const stayingWardsOptionsSelector = createSelector(
  wardsSelector,
  selectedStayingProvinceSelector,
  (allWards, selectedProvince) => allWards[selectedProvince] ?? []
);

// ==============================

export const isShowButtonSubmitEditing = createSelector(
  [
    isEditingPersonalInformationSelector,
    bankInformationChangeDataSelector,
    maritalInformationChangeDataSelector,
    educationInformationChangeDataSelector,
    basicInformationChangeDataSelector,
    residenceInformationDataChangeSelector,
  ],
  (
    isEditing,
    bankChangeData,
    maritalChangeData,
    educationChangeData,
    basicChangeData,
    residenceChangeData
  ) =>
    isEditing &&
    (!isEmpty(bankChangeData) ||
      !isEmpty(maritalChangeData) ||
      !isEmpty(educationChangeData) ||
      !isEmpty(basicChangeData) ||
      !isEmpty(residenceChangeData))
);

// work performance result
const workPerformanceResultSelector = createSelector(
  personnelInformationSelector,
  (state) => state.workPerformanceResult
);

export const workPerformanceResultDataSelector = createSelector(
  workPerformanceResultSelector,
  (state) => state.data
);

export const workPerformanceResultStatusSelector = createSelector(
  workPerformanceResultSelector,
  (state) => state.status
);
// upload file
const uploadFileSlice = createSelector(
  personnelInformationSelector,
  (state) => state.uploadFile
);

export const uploadFileStatusSelector = createSelector(
  uploadFileSlice,
  (state) => state.status
);
export const uploadFileDataSelector = createSelector(
  uploadFileSlice,
  (state) => state.data
);

// timeline history
const timelineHistorySelector = createSelector(
  personnelInformationSelector,
  (state) => state.getHistoryTimeline
);

export const timelineHistoryDataSelector = createSelector(
  timelineHistorySelector,
  (state) => state.data
);

export const timelineHistoryActiveIdSelector = createSelector(
  timelineHistorySelector,
  (state) => state.activeId
);

// detail history
const detailHistorySelector = createSelector(
  personnelInformationSelector,
  (state) => state.getHistoryDetail
);

export const detailHistoryDataSelector = createSelector(
  detailHistorySelector,
  (state) => state.data
);

// check có được sửa thông tin hay không
const checkEditable = createSelector(
  personnelInformationSelector,
  (state) => state.getPermissionEdit
);

export const checkEditableDataSelector = createSelector(
  checkEditable,
  (state) => state.data
);

export const checkEditableStatusSelector = createSelector(
  checkEditable,
  (state) => state.status
);

export const checkFormResidenceDirty = createSelector(
  [
    isOpenDialogSelector(DIALOG.CHANGE_RESIDENCE_INFORMATION),
    residenceInformationDataSelector,
    formEditResidenceInformationDataSelector,
  ],
  (isOpen, originalData, currentData) => {
    return isOpen && !isEmpty(getChangedProperties(originalData, currentData));
  }
);

export const checkFormBasicDirty = createSelector(
  [
    isOpenDialogSelector(DIALOG.CHANGE_BASIC_INFORMATION),
    basicInformationDataSelector,
    formEditBasicInformationDataSelector,
  ],
  (isOpen, originalData, currentData) => {
    return (
      isOpen &&
      !isEmpty(
        getChangedProperties(originalData, currentData, [
          "commit",
          // "_file_contents",
          // "_files",
        ])
      )
    );
  }
);

export const checkFormBankDirty = createSelector(
  [
    isOpenDialogSelector(DIALOG.CHANGE_BANK_INFORMATION),
    bankInformationDataSelector,
    formEditBankInformationDataSelector,
  ],
  (isOpen, originalData, currentData) => {
    return isOpen && !isEmpty(getChangedProperties(originalData, currentData));
  }
);

export const checkFormMaritalDirty = createSelector(
  [
    isOpenDialogSelector(DIALOG.CHANGE_MARITAL_INFORMATION),
    maritalInformationDataSelector,
    formEditMaritalInformationDataSelector,
  ],
  (isOpen, originalData, currentData) => {
    return isOpen && !isEmpty(getChangedProperties(originalData, currentData));
  }
);

export const checkFormEduDirty = createSelector(
  [
    isOpenDialogSelector(DIALOG.CHANGE_EDUCATION_INFORMATION),
    educationInformationDataSelector,
    languageInformationDataSelector,
    certificationInformationDataSelector,
    certificationProfessionalDataSelector,
    formEditEducationInformationDataSelector,
  ],
  (
    isOpen,
    education,
    languages,
    certificateData,
    professionalCertificates,
    currentData
  ) => {
    const originalData = {
      education,
      languages,
      certificates: certificateData.certificates,
      certificateOthers: certificateData.certificateOthers,
      professionalCertificates,
    };
    return (
      isOpen &&
      !isEmpty(
        getChangedProperties(originalData, currentData, [
          "commit",
          "_file_contents",
          "_files",
          "certificates",
        ])
      )
    );
  }
);
